import { failure, success } from 'typegate';
import { FieldTypes } from '../../types/carrierQuestionnaire/fieldTypes';
import { DateType, DetailedMappingFieldTypes, DetailedMappingInputParsingError, inputDetailedMappingDataSchema, } from './detailedMappingTypes';
const blueprintFieldTypesToDetailedMappingFieldTypesMap = {
    [FieldTypes.agree]: DetailedMappingFieldTypes.agree,
    [FieldTypes.autocomplete]: DetailedMappingFieldTypes.input,
    [FieldTypes.button]: undefined,
    [FieldTypes.checkbox]: undefined,
    [FieldTypes.checkboxGroup]: DetailedMappingFieldTypes.checkboxGroup,
    [FieldTypes.currencyCard]: undefined,
    [FieldTypes.date]: DetailedMappingFieldTypes.date,
    [FieldTypes.yearMonth]: undefined,
    [FieldTypes.dropdown]: DetailedMappingFieldTypes.dropdown,
    [FieldTypes.information]: undefined,
    [FieldTypes.input]: DetailedMappingFieldTypes.input,
    [FieldTypes.money]: DetailedMappingFieldTypes.money,
    [FieldTypes.number]: DetailedMappingFieldTypes.number,
    [FieldTypes.phone]: DetailedMappingFieldTypes.phone,
    [FieldTypes.radio]: DetailedMappingFieldTypes.radio,
    [FieldTypes.signature]: undefined,
    [FieldTypes.textarea]: DetailedMappingFieldTypes.textarea,
};
export class PopulateAnswersError extends Error {
    _tag = 'PopulateAnswersError';
}
export class DetailedMappingInterpreter {
    answersResolver;
    blueprint;
    constructor(answersResolver) {
        this.answersResolver = answersResolver;
        this.blueprint = answersResolver.getBlueprint();
    }
    getOutput(language, outputKeyType = 'blueprintId') {
        const output = {};
        for (const key in this.blueprint.sectionGroupBlueprints) {
            const sectionGroupKey = key;
            const sectionGroup = this.blueprint.sectionGroupBlueprints[sectionGroupKey];
            const sections = this.blueprint.sectionBlueprints.filter((sectionBlueprint) => sectionBlueprint.sectionGroupKey === sectionGroupKey);
            if (sectionGroup?.repeatable) {
                const repeatableSectionGroupOutput = {
                    type: 'collection',
                    id: sectionGroup.id,
                    children: [],
                };
                const repeatableOutputKey = outputKeyType === 'blueprintId' ? sectionGroup.id : sectionGroup.repeatable.outputPath || sectionGroup.id;
                output[repeatableOutputKey] = repeatableSectionGroupOutput;
                const collectionInOutput = output[repeatableOutputKey];
                const repetitionCount = this.answersResolver.usingNodeId().getRepetitionCount(sectionGroup.repeatable.repeatableAnswerNodeId, {}) ||
                    0;
                if (repetitionCount) {
                    collectionInOutput.children = [];
                }
                for (let i = 0; i < repetitionCount; i++) {
                    collectionInOutput.children[i] = {
                        type: 'collectionItem',
                        data: {},
                    };
                    const collectionItemInOutput = collectionInOutput.children[i];
                    const surrogateIdResult = this.answersResolver
                        .usingNodeId()
                        .getInstanceId(sectionGroup.repeatable.repeatableAnswerNodeId, {
                        [sectionGroup.repeatable.repeatableAnswerNodeId]: i,
                    });
                    if (surrogateIdResult.success) {
                        collectionItemInOutput.surrogateId = surrogateIdResult.value;
                    }
                    const scopes = {
                        [sectionGroup.repeatable.repeatableAnswerNodeId]: i,
                    };
                    for (const section of sections) {
                        this.getSectionOutput(outputKeyType, language, section, collectionItemInOutput.data, scopes);
                    }
                }
            }
            else {
                for (const section of sections) {
                    this.getSectionOutput(outputKeyType, language, section, output, {});
                }
            }
        }
        return success(output);
    }
    getSectionOutput(outputKeyType, language, section, outputParent, scopes) {
        for (const subsection of section.subsections) {
            for (const fieldGroup of subsection.fieldGroups) {
                if (fieldGroup?.repeatable) {
                    const repeatableOutputPath = outputKeyType === 'blueprintId' ? fieldGroup.id : fieldGroup.repeatable.outputPath || fieldGroup.id;
                    const repeatableFieldGroupOutput = {
                        type: 'collection',
                        id: fieldGroup.id,
                        children: [],
                    };
                    outputParent[repeatableOutputPath] = repeatableFieldGroupOutput;
                    const fieldGroupInOutput = outputParent[repeatableOutputPath];
                    const repetitionCount = this.answersResolver
                        .usingNodeId()
                        .getRepetitionCount(fieldGroup.repeatable.repeatableAnswerNodeId, scopes) || 0;
                    for (let i = 0; i < repetitionCount; i++) {
                        fieldGroupInOutput.children[i] = {
                            data: {},
                            type: 'collectionItem',
                        };
                        const surrogateIdResult = this.answersResolver
                            .usingNodeId()
                            .getInstanceId(fieldGroup.repeatable.repeatableAnswerNodeId, {
                            ...scopes,
                            [fieldGroup.repeatable.repeatableAnswerNodeId]: i,
                        });
                        if (surrogateIdResult.success) {
                            fieldGroupInOutput.children[i].surrogateId = surrogateIdResult.value;
                        }
                        const fieldGroupScopes = {
                            ...scopes,
                            [fieldGroup.repeatable.repeatableAnswerNodeId]: i,
                        };
                        for (const field of fieldGroup.fields) {
                            this.getFieldOutput(outputKeyType, language, field, fieldGroupInOutput.children[i].data, fieldGroupScopes);
                        }
                    }
                }
                else {
                    for (const field of fieldGroup.fields) {
                        this.getFieldOutput(outputKeyType, language, field, outputParent, scopes);
                    }
                }
            }
        }
    }
    getFieldOutput(outputKeyType, language, field, outputParent, scopes) {
        const outputPath = outputKeyType === 'blueprintId' ? field.id : field.outputPath || field.id;
        const answer = this.answersResolver.usingNodeId().getAnswer(field.answerNodeId, scopes);
        const type = blueprintFieldTypesToDetailedMappingFieldTypesMap[field.fieldType];
        if (!type || (!answer && answer !== 0 && answer !== false)) {
            return;
        }
        const outputField = {
            value: answer,
            questionText: field.title?.[language],
            questionSubtext: field.text?.[language],
            type,
            id: field.id,
        };
        if (outputField.type === DetailedMappingFieldTypes.date) {
            // For now, always date, because it is actually handled through validation
            outputField.dateType = DateType.date;
        }
        if (outputField.type === DetailedMappingFieldTypes.dropdown ||
            outputField.type === DetailedMappingFieldTypes.radio) {
            const options = field.selectOptions;
            const displayValue = options.find((o) => o.value === answer)?.text?.[language];
            outputField.displayValue = displayValue || '';
        }
        if (outputField.type === DetailedMappingFieldTypes.checkboxGroup) {
            const options = field.selectOptions;
            const values = Array.isArray(outputField.value) ? outputField.value : [outputField.value];
            outputField.value = values;
            const displayValues = outputField.value.map((value) => {
                return options.find((o) => o.value === value)?.text?.[language];
            });
            outputField.displayValue = displayValues;
        }
        outputParent[outputPath] = outputField;
    }
    populateAnswers(answers) {
        const parsedAnswersResult = inputDetailedMappingDataSchema.parse(answers);
        if (!parsedAnswersResult.success) {
            return failure(new DetailedMappingInputParsingError(parsedAnswersResult.error));
        }
        const parsedAnswers = parsedAnswersResult.value;
        this.answersResolver.clearAnswers();
        const stack = [];
        stack.push({
            node: parsedAnswers,
            nodeType: 'inputData',
            scopes: {},
        });
        while (stack.length) {
            const frame = stack.pop();
            if (!frame) {
                break;
            }
            if (frame.nodeType === 'inputData') {
                for (const key in frame.node) {
                    const childNode = frame.node[key];
                    const type = childNode.type;
                    if (type === 'collection') {
                        stack.push({
                            node: childNode,
                            nodeType: 'collection',
                            scopes: frame.scopes,
                        });
                    }
                    else {
                        stack.push({
                            node: childNode,
                            nodeType: 'field',
                            scopes: frame.scopes,
                        });
                    }
                }
            }
            if (frame.nodeType === 'collectionItem') {
                const node = frame.node;
                for (const childKey in node.data) {
                    const child = node.data[childKey];
                    if (child.type === 'collection') {
                        stack.push({
                            node: child,
                            nodeType: 'collection',
                            scopes: frame.scopes,
                        });
                    }
                    else {
                        stack.push({
                            node: child,
                            nodeType: 'field',
                            scopes: frame.scopes,
                        });
                    }
                }
            }
            if (frame.nodeType === 'field') {
                try {
                    this.answersResolver.setAnswer(frame.node.value, frame.node.id, frame.scopes);
                }
                catch (e) {
                    return failure(new PopulateAnswersError(e.message));
                }
            }
            if (frame.nodeType === 'collection') {
                const collectionId = frame.node.id;
                for (const [i, child] of frame.node.children.entries()) {
                    if (child.surrogateId) {
                        try {
                            this.answersResolver.usingNodeId().setInstanceId(collectionId, { [collectionId]: i }, child.surrogateId);
                        }
                        catch (e) {
                            return failure(new PopulateAnswersError(e.message));
                        }
                        // In the future, if there is a performance problem, we can look at using a pointer to the parent frame and defining only the change in scope from the parent.
                        const scopes = { ...frame.scopes, [collectionId]: i };
                        stack.push({
                            node: child,
                            nodeType: 'collectionItem',
                            scopes,
                        });
                    }
                }
            }
        }
        return success(undefined);
    }
}
